import type { LocalPrivateSpecialistsConfig } from './config';

/**
 * Satisfaction smoothed toward a prior, so 100% from 1 review does not outrank 95% from 299.
 * score = (satisfaction * count + PRIOR * WEIGHT) / (count + WEIGHT)
 */
const PRIOR_SATISFACTION = 80;
const PRIOR_WEIGHT = 20;

export const smoothedSatisfaction = (satisfaction?: number | null, count?: number | null) => {
  if (satisfaction == null || !count) return null;
  return (satisfaction * count + PRIOR_SATISFACTION * PRIOR_WEIGHT) / (count + PRIOR_WEIGHT);
};

/**
 * Search-side pre-filter for "has an in-person turn": online-booking doctor (not phone/bulk) with a
 * first free turn in the index. The index can be stale (it still lists turns of doctors whose booking
 * just closed), so the final say is Saman's `booking_available` — see LocalPrivateSpecialists.
 */
export const hasIndexedInPersonTurn = (hit: any) => !hit?.is_bulk && !!hit?.presence_freeturn;

/** «<span>اولین نوبت: <b>کمتر از یک ساعت دیگر</b></span>» of the search's booking action → plain text. */
export const firstTurnLabel = (hit: any): string => {
  const action = (hit?.actions ?? []).find((item: any) => /اولین نوبت/.test(item?.top_title ?? ''));
  return (action?.top_title ?? '').replace(/<[^>]*>/g, '').trim();
};

/**
 * Keeps doctors of the configured specialty, drops the profile's own doctor, and orders by smoothed
 * satisfaction (doctors without reviews last, in the search's own order).
 */
export const rankLocalSpecialists = (results: any[] | undefined, config: LocalPrivateSpecialistsConfig, ownSlug?: string) => {
  const ownUrl = ownSlug ? `/dr/${ownSlug}/` : null;
  const doctors = (results ?? []).filter(hit => {
    if (hit?.type && hit.type !== 'doctor') return false;
    if (ownUrl && (hit?.url === ownUrl || hit?.slug === ownSlug)) return false;
    const expertise: string = hit?.display_expertise ?? '';
    if (!config.includeExpertise.some(text => expertise.includes(text))) return false;
    if (config.excludeExpertise?.some(text => expertise.includes(text))) return false;
    return true;
  });

  return doctors
    .map((hit, index) => ({ hit, index, score: smoothedSatisfaction(hit?.satisfaction, hit?.rates_count) }))
    .sort((a, b) => {
      if (a.score == null && b.score == null) return a.index - b.index;
      if (a.score == null) return 1;
      if (b.score == null) return -1;
      return b.score - a.score || a.index - b.index;
    })
    .map(item => item.hit);
};
